import { localizedStringForLocale } from "src/utils/mapEntertainment";
import { resolvePayloadMediaPath } from "src/utils/resolvePayloadMediaPath";

const BLOCK_TYPE_MAP = {
  diningPromoHero: "Hero",
  diningPromoTitleSection: "TitleSection",
  diningPromoTextContent: "TextContent",
  diningPromoImageSection: "ImageSection",
  diningPromoSmallTextContent: "SmallTextContent",
  diningPromoSplitSection: "SplitSection",
  diningPromoStackSection: "SplitSection",
  diningPromoImageRow: "SplitSection",
  diningPromoChoicePair: "SplitSection",
};

/** @param {unknown} value @param {string} locale */
const resolveLangToken = (value, locale) => {
  if (typeof value !== "string" || !value.trim()) return "";
  return value.replace(/\{lang\}/g, locale).replace(/\{lng\}/g, locale);
};

/** @param {unknown} raw @param {string} locale */
const phrasesFromCms = (raw, locale) => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      if (typeof item === "string") return item;
      if (!item || typeof item !== "object") return "";
      return localizedStringForLocale(item.phrase ?? item, locale);
    })
    .filter(Boolean);
};

/**
 * Prefer explicit CDN path fields from CMS (authoritative for dining promos).
 * Upload objects are fallback only — resolvePayloadMediaPath can mangle
 * filenames like `Previews-25-01-15.jpg` via uniquify stripping.
 * @param {unknown} upload
 * @param {unknown} pathField
 * @param {string} locale
 */
const resolveMediaOrPath = (upload, pathField, locale) => {
  const fromPath = resolveLangToken(localizedStringForLocale(pathField, locale), locale);
  if (fromPath) return fromPath;
  return resolvePayloadMediaPath(upload, locale) || "";
};

/** @param {Record<string, unknown>} block @param {string} locale */
const mapBlockProps = (block, locale) => {
  const type = block?.blockType;
  if (type === "diningPromoHero") {
    return {
      bgImage: resolveMediaOrPath(block.bgImage, block.bgImagePath, locale),
      image: resolveMediaOrPath(block.image, block.imagePath, locale),
      description: localizedStringForLocale(block.description, locale),
      descriptionBgColor: block.descriptionBgColor || "#0000004d",
      darkenBg: block.darkenBg ?? 0,
      buttonSettings: block.buttonSettings ?? null,
      className: block.className || undefined,
    };
  }

  if (type === "diningPromoTitleSection") {
    return {
      as: block.as || "h2",
      subTitle: localizedStringForLocale(block.subTitle, locale),
      text: localizedStringForLocale(block.text, locale) || undefined,
      className: block.className || undefined,
    };
  }

  if (type === "diningPromoTextContent") {
    return {
      content: localizedStringForLocale(block.content, locale),
      className: block.className || "",
      routerAware: Boolean(block.routerAware),
      strongHighlightPhrases: phrasesFromCms(block.strongHighlightPhrases, locale),
      strongUnderlinePhrases: phrasesFromCms(block.strongUnderlinePhrases, locale),
    };
  }

  if (type === "diningPromoImageSection") {
    return {
      src: resolveMediaOrPath(block.src, block.srcPath, locale),
      alt: localizedStringForLocale(block.alt, locale),
      className: block.className || undefined,
    };
  }

  if (type === "diningPromoSmallTextContent") {
    return {
      content: localizedStringForLocale(block.content, locale),
      className: block.className || undefined,
    };
  }

  if (type === "diningPromoSplitSection" || type === "diningPromoStackSection") {
    const mapNested = (sections) =>
      (Array.isArray(sections) ? sections : [])
        .map((nested) => mapSectionBlock(nested, locale))
        .filter(Boolean);

    const leftSections =
      type === "diningPromoStackSection"
        ? mapNested(block.sections)
        : mapNested(block.leftSections);

    return {
      withBackground: block.withBackground !== false,
      backgroundColor: block.backgroundColor || undefined,
      className: block.className || undefined,
      leftColumnClassName: block.leftColumnClassName || undefined,
      rightColumnClassName: block.rightColumnClassName || undefined,
      leftSections,
      rightSections: mapNested(block.rightSections),
    };
  }

  if (type === "diningPromoImageRow" || type === "diningPromoChoicePair") {
    const images = (
      Array.isArray(block.images) && block.images.length
        ? block.images
        : [
            {
              src: resolveMediaOrPath(block.leftImage, block.leftImagePath, locale),
              alt: localizedStringForLocale(block.leftAlt, locale),
            },
            {
              src: resolveMediaOrPath(block.rightImage, block.rightImagePath, locale),
              alt: localizedStringForLocale(block.rightAlt, locale),
            },
          ]
    )
      .map((item) => {
        if (!item || typeof item !== "object") return null;
        const src = resolveMediaOrPath(
          item.src || item.image,
          item.srcPath || item.imagePath,
          locale,
        );
        if (!src && !item.src) return null;
        return {
          src: src || item.src,
          alt: localizedStringForLocale(item.alt, locale) || item.alt || "",
        };
      })
      .filter((image) => image?.src);

    const imageBlock = (image) => ({
      type: "ImageSection",
      props: { src: image.src, alt: image.alt },
    });

    return {
      withBackground: false,
      className: ["with-connector", block.className].filter(Boolean).join(" "),
      leftSections: images[0] ? [imageBlock(images[0])] : [],
      rightSections: images[1] ? [imageBlock(images[1])] : [],
    };
  }

  return {};
};

/** @param {Record<string, unknown>} block @param {string} locale */
export const mapSectionBlock = (block, locale) => {
  if (!block || typeof block !== "object") return null;
  const type = BLOCK_TYPE_MAP[block.blockType];
  if (!type) return null;
  return {
    type,
    props: mapBlockProps(block, locale),
  };
};

/**
 * @param {Record<string, unknown>} doc
 * @param {string} [locale]
 */
export function mapRestaurantPromoDoc(doc, locale = "en") {
  const lang = locale === "fr" ? "fr" : "en";
  const sections = (Array.isArray(doc?.sections) ? doc.sections : [])
    .map((block) => mapSectionBlock(block, lang))
    .filter(Boolean);

  const theme = {
    ...(doc?.highlightColor ? { highlightColor: doc.highlightColor } : {}),
    ...(doc?.titleHighlightColor ? { titleHighlightColor: doc.titleHighlightColor } : {}),
    ...(doc?.markdownHighlightColor
      ? { markdownHighlightColor: doc.markdownHighlightColor }
      : {}),
    ...(doc?.markdownVariant ? { markdownVariant: doc.markdownVariant } : {}),
  };

  return {
    id: doc?.id,
    promotionKey: doc?.promotionKey,
    slug: localizedStringForLocale(doc?.slug, lang),
    pageTitle: localizedStringForLocale(doc?.pageTitle, lang),
    publishAt: doc?.publishAt || undefined,
    unpublishAt: doc?.unpublishAt || undefined,
    theme: Object.keys(theme).length ? theme : undefined,
    sections,
  };
}

/** @param {ReturnType<typeof mapRestaurantPromoDoc>|null|undefined} page */
export function isMeaningfulRestaurantPromo(page) {
  return Boolean(
    page?.promotionKey &&
      Array.isArray(page?.sections) &&
      page.sections.length > 0,
  );
}
